<template>
    <TransitionRoot appear :show="isOpenDialog" as="template">
        <Dialog as="div" class="dialog-box relative z-10" @close="closeModal">
            <TransitionChild
                as="template"
                enter="duration-300 ease-out"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black bg-opacity-25" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-y-auto">
                <div
                    class="flex min-h-full items-center justify-center p-4 text-center"
                >
                    <TransitionChild
                        as="template"
                        enter="duration-300 ease-out"
                        enter-from="opacity-0 scale-95"
                        enter-to="opacity-100 scale-100"
                        leave="duration-200 ease-in"
                        leave-from="opacity-100 scale-100"
                        leave-to="opacity-0 scale-95"
                    >
                        <DialogPanel
                            class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
                        >
                            <DialogTitle
                                as="h3"
                                :class="['text-lg', 'font-medium', 'leading-6', setting.style.title,]"
                            >
                                {{ setting.title }}
                            </DialogTitle>
                            <div class="mt-2">
                                <p :class="['text-sm', 'md:whitespace-nowrap', setting.style.message]" v-html="dialogMessage" />
                            </div>
                            <div class="mt-4">
                                <button
                                    type="button"
                                    class="inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                                    :class="[setting.style.button.background, setting.style.button.color, setting.style.button.hoverBg, setting.style.button.focusVisibleRing]"
                                    @click="closeModal"
                                >
                                    Close
                                </button>
                            </div>
                            <span class="safe-style" />
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import { watch, ref, computed } from "vue";
import {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogPanel,
    DialogTitle
} from "@headlessui/vue";
import { onMounted } from "vue";

export default {
    name: "DialogBox",
    components: {
        TransitionRoot,
        TransitionChild,
        Dialog,
        DialogPanel,
        DialogTitle
    },
    props: {
        status: { type: Boolean, default: false },
        message: { type: String, default: '' },
        title: { type: String, default: 'Notice' },
        reload: { type: Boolean, default: false },
        type: { type: String, default: 'normal' }
    },
    emits: [ "close-modal" ],
    setup(props, { emit }) {
        const isOpenDialog = ref(false);
        const dialogMessage = ref('');
        const dialogTitle = ref('Notice');
        const settingData = {
            success: {
                color: 'green',
                title: 'Success',
            },
            warning: {
                color: 'yellow',
                title: 'Warning',
            },
            fatal: {
                color: 'red',
                title: 'Warning',
            },
            normal: {
                color: 'green',
                title: 'Success',
            }
        }
        const setting = ref(settingData.normal)
        const reload = computed(() => props.reload);

        onMounted(() => {
            updateStyle()
        })

        watch(
            () => props.status,
            () => {
                isOpenDialog.value = props.status
            }
        )

        watch(
            () => props.message,
            () => {
                dialogMessage.value = props.message
            }
        )

        watch(
            () => props.type,
            () => {
                setting.value = settingData[props.type] ?? settingData.normal
                updateStyle()
            }
        )

        const closeModal = () => {
            isOpenDialog.value = false
            if (reload.value === true) {
                window.location = window.location.href
            }
            emit('close-modal', false)
        }

        const updateStyle = () => {
            setting.value.style = {
                title: `text-${setting.value.color}-900`,
                message: `text-${setting.value.color}-400`,
                button: {
                    background: `bg-${setting.value.color}-100`,
                    color: `text-${setting.value.color}-900`,
                    hoverBg: `hover:bg-${setting.value.color}-200`,
                    focusVisibleRing: `focus-visible:ring-${setting.value.color}-500`,
                }
            }
        }

        return {
            isOpenDialog,
            dialogMessage,
            dialogTitle,
            closeModal,
            setting
        };
    },
};
</script>

<style lang="scss">
.safe-style {
    @apply text-green-200 text-green-400 bg-green-100 hover:bg-green-200 focus-visible:ring-green-500 text-yellow-200 text-yellow-400 bg-yellow-100 hover:bg-yellow-200 focus-visible:ring-yellow-500 text-red-200 text-red-400 bg-red-100 hover:bg-red-200 focus-visible:ring-red-500 text-gray-200 text-gray-400 bg-gray-100 hover:bg-gray-200 focus-visible:ring-gray-500;
}

.dialog-box {
    &__title {
        &__green {
            @apply text-green-900
        }
    }
}
</style>
